import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '../../components/layouts/AppLayout'
import {
  XCircle,
  Clock,
  TrendDown,
  MapPin,
  ArrowRight,
  Eye,
  WarningCircle
} from '@phosphor-icons/react'
import { fetchLostOffers, type LostOffer, type LostOffersSummary } from '../../services/lostOffers'
import { formatDateBR } from '../../lib/date'
import { MultiCurrencyTotal } from '../../components/offer/MultiCurrencyTotal'
import { totalsFromLegacyOffer } from '../../utils/offerTotals'

function statusBadge(status: 'declined' | 'expired') {
  if (status === 'declined') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle size={12} weight="fill" />Recusada</span>
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700"><Clock size={12} weight="fill" />Expirada</span>
}

export default function LostOffersPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<LostOffer[]>([])
  const [summary, setSummary] = useState<LostOffersSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'declined' | 'expired'>('all')

  useEffect(() => {
    fetchLostOffers()
      .then(({ items, summary }) => { setItems(items); setSummary(summary) })
      .catch((err) => console.error('Failed to load lost offers', err))
      .finally(() => setLoading(false))
  }, [])

  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter)

  return (
    <AppLayout>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendDown size={26} className="text-red-500" weight="duotone" />
            Propostas Perdidas
          </h1>
          <p className="text-sm text-gray-500 mt-1">Histórico de propostas recusadas ou expiradas, com o seu ranking.</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{summary.total}</div>
              <div className="text-xs text-gray-500 mt-1">propostas perdidas</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs text-red-700 uppercase tracking-wide">Recusadas</div>
              <div className="text-3xl font-bold text-red-700 mt-1">{summary.declined}</div>
              <div className="text-xs text-gray-500 mt-1">pelo embarcador</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs text-orange-700 uppercase tracking-wide">Expiradas</div>
              <div className="text-3xl font-bold text-orange-700 mt-1">{summary.expired}</div>
              <div className="text-xs text-gray-500 mt-1">prazo vencido</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs text-gray-700 uppercase tracking-wide">Diferença Média</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">
                {summary.avgDifferencePercent != null ? `+${summary.avgDifferencePercent}%` : '—'}
              </div>
              <div className="text-xs text-gray-500 mt-1">acima do vencedor</div>
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4">
          {(['all', 'declined', 'expired'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                filter === f
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
            >
              {f === 'all' ? 'Todas' : f === 'declined' ? 'Recusadas' : 'Expiradas'}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-500">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500 bg-gray-50 rounded-lg">
            <TrendDown size={40} className="mx-auto text-gray-300 mb-2" />
            Nenhuma proposta perdida ainda.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(offer => {
              return (
                <div key={offer.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{offer.shipperTradingName || offer.shipperName}</span>
                        {statusBadge(offer.status)}
                        <span className="text-xs text-gray-500 font-mono">{offer.quoteTxControl}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-600">
                        <MapPin size={12} className="text-gray-400" />
                        <span>{offer.origin?.location?.city || '?'}</span>
                        <ArrowRight size={12} className="text-gray-400" />
                        <span>{offer.destination?.location?.city || '?'}</span>
                        {offer.modalityName && <span className="mx-1 text-gray-300">•</span>}
                        {offer.modalityName && <span>{offer.modalityName}</span>}
                        {offer.incoterm && <><span className="mx-1 text-gray-300">•</span><span>{offer.incoterm}</span></>}
                      </div>
                      {offer.declinedReason && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded text-xs text-red-700 flex items-start gap-1.5">
                          <WarningCircle size={14} className="flex-shrink-0 mt-0.5" />
                          <span><strong>Motivo:</strong> {offer.declinedReason}</span>
                        </div>
                      )}
                    </div>

                    <div className="text-right">
                      <div className="text-xs text-gray-500 mb-1">Sua proposta</div>
                      <MultiCurrencyTotal
                        totalsByCurrency={totalsFromLegacyOffer(offer)}
                        variant="compact"
                      />
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100 text-sm flex items-start gap-10">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Ranking</div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="font-semibold text-gray-900">{offer.rankPosition}º</span>
                        <span className="text-xs text-gray-500">de {offer.totalCompetitors}</span>
                      </div>
                    </div>
                    {offer.differencePercent != null && offer.differencePercent > 0 && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Diferença</div>
                        <div className="flex items-center gap-1 mt-1">
                          <span className="font-semibold text-red-600">+{offer.differencePercent}%</span>
                          <span className="text-xs text-gray-500">acima do vencedor</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                    <span>Criada em {formatDateBR(offer.createdAt)}</span>
                    <button
                      onClick={() => navigate(`/freight-forwarder/quotes/${offer.quoteId}/view`)}
                      className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
                    >
                      <Eye size={14} />
                      Ver cotação
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>
    </AppLayout>
  )
}
